import { createContext, useState } from 'react';
import { createTheme, type ThemeOptions, type PaletteMode, type Theme } from '@mui/material/styles';
import { colorTokens } from './colors';

declare module '@mui/material/styles' {
  interface Palette {
    banner: {
      main: string;
      contrastText: string;
    };
  }

  interface PaletteOptions {
    banner?: {
      main?: string;
      contrastText: string;
    };
  }

  interface Theme {
    filters: {
      iconGlow: string;
    };
  }

  interface ThemeOptions {
    filters?: {
      iconGlow?: string;
    };
  }
}

export const toPaletteMode = (value: string): PaletteMode => {
  return value === 'dark' || value === 'light' ? value : 'light';
};

export const themeSettings = (mode: string): ThemeOptions => {
  const colors = colorTokens(mode);
  const darkOptions = {
      primary: {
        main: colors.primary[500],
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary[600],
      },
      info: {
        main: colors.info[500],
        contrastText: '#ffffff',
      },
      success: {
        main: colors.success[500],
        contrastText: '#ffffff',
      },
      neutral: {
        dark: colors.surface[900],
        main: colors.surface[800],
        light: colors.surface[700],
      },
      background: {
        default: "#0d131f",
        paper: "#0f1624",
      },
      banner: {
        main: colors.banner.main,
        contrastText: colors.primary[500],
      },
    };

    const lightOptions = {
      primary: {
        main: colors.primary[400],
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary[600],
      },
      info: {
        main: colors.info[400],
        contrastText: '#ffffff',
      },
      success: {
        main: colors.success[400],
        contrastText: '#ffffff',
      },
      neutral: {
        dark: colors.surface[700],
        main: colors.surface[800],
        light: colors.surface[900],
      },
      background: {
        default: "#fcfcfc",
      },
      banner: {
        main: colors.banner.main,
        contrastText: colors.primary[500],
      },
    };


  return {
    palette: {
      mode: toPaletteMode(mode),
      ...(mode === 'dark'? darkOptions : lightOptions
      )
    },
    filters: {
      iconGlow: mode === 'dark'? 
        // Used resource to generate the lines below: https://codepen.io/sosuke/pen/Pjoqqp
        'invert(7%) sepia(89%) saturate(5723%) hue-rotate(332deg) brightness(97%) contrast(111%) drop-shadow(0 0 2px #003b57)' :
        'invert(15%) sepia(7%) saturate(26%) hue-rotate(18deg) brightness(103%) contrast(95%) drop-shadow(0 0 3px #dd7796)'
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 14,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    }
  };
};

export interface ColorModeContextType {
  toggleColorMode: () => void;
}

export const ColorModeContext = createContext<ColorModeContextType>({
  toggleColorMode: (): void => { }
});

export const useMode = (): [Theme, ColorModeContextType] => {
  const [mode, setMode] = useState<PaletteMode>("light");

  const colorMode = {
    toggleColorMode: (): void => setMode((prev) => (prev === "light" ? "dark" : "light")),
  };

  const theme = createTheme(themeSettings(mode));
  return [theme, colorMode];
};
